import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import classnames from 'classnames';
import { useTPPServices } from '../../../../tppServices/tppDIHooks';
import { generateSearchPath as generateEnginesSearchPath } from '../../../../utils/urlHelpers/engines';
import { generateSearchPath } from '../../../../utils/urlHelpers/boats';
import { getPathnameLanguage } from '../../../../utils/language';
import { generateBlogPath } from '../../../../utils/urlHelpers/blog';
import { getFSBOUrl } from '../../../../utils/urlHelpers/fsbo';
import { definedValue, yieldToMain } from '../../../../utils';
import { getAssetUrl } from '../../../../utils/commonHelper';
import { getMessages } from '../../../../tppServices/translations/messages';
import { setNavigationEvent } from '../../../../store/actions/dataLayer';

const NavBar = (props) => {

    const [open, setOpen] = useState(false);
    const [isThrottled, setIsThrottled] = useState(false);

    const dispatch = useDispatch();

    const intl = useIntl();
    const { formatMessage: t } = intl;
    const messages = getMessages();

    const {
        isPageLoaded,
        currentLanguage,
        isTopNavigationV2Enabled,
        context,
        fullName
    } = props;

    const { routesConstants } = useTPPServices();

    const trackGenericClick = (event) => {
        if (event) {
            dispatch(setNavigationEvent(event));
        }
    };

    const toggleOpen = useCallback(async () => {
        setIsThrottled(true);
        await yieldToMain();
        setOpen((open) => !open);
        requestAnimationFrame(() => {
            setIsThrottled(false);
        });
    }, []);

    const generatePathFromRoute = (route) => {
        const routes = routesConstants;
        if (typeof route === 'object') {
            if (route.search) {
                if (route.type === 'engine') {
                    return generateEnginesSearchPath({}, route.search, true);
                }
                return generateSearchPath({}, route.search, true);
            }
            if (route.article) {
                return `${getPathnameLanguage()}${route.article}`;
            }
            if (route.blog) {
                return generateBlogPath(route.blog);
            }
        }
        if (typeof route === 'string' && ['SELL_URL_ROOT', 'SELL_ENGINES_URL_ROOT'].indexOf(route) >= 0) {
            return getFSBOUrl(route);
        }
        return routes[route];
    };

    const getHeaderLinks = (context) => {
        if (isTopNavigationV2Enabled) {
            return definedValue(context.linksV2, []);
        }
        return definedValue(context.links, []);
    };

    const fsboCTA = definedValue(context.fsboCTA, false);
    const sellBoatUrl = getFSBOUrl();
    const headerLinks = getHeaderLinks(context);

    // Hook to remove the "disable-actions" class from the "hamburguer" button.
    useEffect(() => {
        const handlePageLoad = () => {
            document.getElementById('hdr-nav-tgl-btn')?.classList.remove('disable-actions');
        };
        document.readyState === 'complete' ? handlePageLoad() : window.addEventListener('load', handlePageLoad);
        return () => {
            window.removeEventListener('load', handlePageLoad);
        };
    }, []);

    const toggleClassnames = useMemo(() => classnames({
        'nav-open': open
    }), [open, isPageLoaded]);

    return (
        <>
            <nav id="main-nav" className={toggleClassnames}>
                {fsboCTA && (
                    <div className="cta-wrapper">
                        <a
                            id="nav-fsbo-cta"
                            className="nav-cta"
                            href={sellBoatUrl}
                            onClick={() => {
                                trackGenericClick({
                                    action: 'sell your boat',
                                    label: 'sell your boat'
                                });
                            }}
                        >
                            {t(messages.header.callToAction)}
                        </a>
                    </div>
                )}
                <ul className="header-main-nav">
                    {headerLinks.map(({ name, route, links, event, languages, exceptLanguages, classNames }, index) => {
                        if (languages && !languages.includes(currentLanguage)) {
                            return null;
                        }
                        if (exceptLanguages && exceptLanguages.includes(currentLanguage)) {
                            return null;
                        }
                        const childrenLinks = links?.filter((link) =>
                            !link.languages || link.languages.includes(currentLanguage)
                        );
                        const hasChildren = Boolean(childrenLinks && childrenLinks.length);
                        return (
                            <li key={index} className={classnames('nav-bar-li', {'has-children': hasChildren}, classNames)}>
                                <a
                                    href={generatePathFromRoute(route)}
                                    onClick={() => {
                                        trackGenericClick(event);
                                    }}>
                                    {t(messages.header[name])}
                                </a>
                                {hasChildren && (
                                    <ul className="drop-list">
                                        {childrenLinks.map(({ name, route, event, target, rel }, index) => {
                                            return (
                                                <li key={index}>
                                                    <a
                                                        href={generatePathFromRoute(route)}
                                                        onClick={() => {
                                                            trackGenericClick(event);
                                                        }}
                                                        target={target}
                                                        rel={rel}
                                                    >
                                                        {t(messages.header[name])}
                                                    </a>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                )}
                            </li>
                        );
                    })}
                </ul>
            </nav>
            <header className="main nav-slide">
                <button
                    id="hdr-nav-tgl-btn"
                    onClick={() => {
                        // Both "isPageLoaded" and "isThrottled" have always the same value:
                        // "true" and "false" respectively, so this line could be modified.
                        /* istanbul ignore next */
                        (isPageLoaded && !isThrottled) ? toggleOpen() : undefined;
                    }}
                    className="navbar-toggle disable-actions"
                    disabled={!isPageLoaded || isThrottled}
                >
                    <span className="visuallyhidden">{t(messages.header.toggle)}</span>
                </button>
                <a href={routesConstants.HOME_URL_ROOT} className="logo-link">
                    <img
                        className="logo"
                        src={getAssetUrl(context.logo)}
                        alt={fullName}
                    />
                </a>
                <a href={routesConstants.HOME_URL_ROOT} className="logo-mobile-link">
                    <img
                        className="logo-mobile"
                        src={getAssetUrl(context.logoMobile || context.logo)}
                        alt={fullName}
                    />
                </a>
                <a href={routesConstants.HOME_URL_ROOT} className="logo-nav-link">
                    <img
                        className="logo-nav"
                        src={getAssetUrl(context.logoNav || context.logo)}
                        alt={fullName}
                    />
                </a>
            </header>
        </>
    );
};

NavBar.propTypes = {
    context: PropTypes.object,
    currentLanguage: PropTypes.string,
    isPageLoaded: PropTypes.bool,
    isTopNavigationV2Enabled: PropTypes.bool,
    fullName: PropTypes.string
};

export default NavBar;
